import { Link, useNavigate } from 'react-router-dom';
import { MarketingLayout } from '@/components/MarketingLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GUIDES } from '@/data/guides';
import { ArrowRight, BookOpen } from 'lucide-react';

export default function GuidesIndexPage() {
  const navigate = useNavigate();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'VendorSuite Guides',
    description: 'Guides and playbooks on vendor compliance automation for property managers.',
    url: 'https://vendorsuite.io/guides',
    hasPart: GUIDES.map((g) => ({
      '@type': 'Article',
      headline: g.title,
      url: `https://vendorsuite.io/guides/${g.slug}`,
    })),
  };

  return (
    <MarketingLayout>
      <Seo
        title="Vendor Compliance Guides for Property Managers"
        description="Practical guides on tracking COI expiration, building a vendor compliance checklist, and automating vendor management across a property portfolio."
        path="/guides"
        jsonLd={jsonLd}
      />
      <div data-testid="guides-index-page" className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-12">
          <Badge variant="secondary" className="mb-4">Resources</Badge>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Vendor compliance guides for property managers
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Practical playbooks for tracking insurance, licenses, and tax documents across your entire vendor portfolio—without the spreadsheets.
          </p>
        </div>

        <div className="space-y-5">
          {GUIDES.map((g) => (
            <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`}>
              <Card className="rounded-2xl border hover:border-primary/50 transition-colors group cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium text-primary">{g.tag}</span>
                  </div>
                  <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {g.title}
                  </h2>
                  <p className="text-muted-foreground text-sm mb-3">{g.excerpt}</p>
                  <span className="text-sm text-primary font-medium inline-flex items-center">
                    Read guide <ArrowRight className="ml-1 h-3 w-3" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}

          <Link to="/compare/vendorsuite-vs-netvendor" data-testid="guide-card-compare">
            <Card className="rounded-2xl border hover:border-primary/50 transition-colors group cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <span className="text-xs font-medium text-primary">Comparison</span>
                </div>
                <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  VendorSuite vs NetVendor: How to Choose Vendor Compliance Software
                </h2>
                <p className="text-muted-foreground text-sm mb-3">
                  A feature-by-feature framework for evaluating vendor compliance platforms for property management.
                </p>
                <span className="text-sm text-primary font-medium inline-flex items-center">
                  Read comparison <ArrowRight className="ml-1 h-3 w-3" />
                </span>
              </CardContent>
            </Card>
          </Link>
        </div>

        <div className="mt-16 text-center rounded-2xl border bg-muted/30 p-10">
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Put these playbooks on autopilot
          </h2>
          <p className="text-muted-foreground mb-6">Start a free 30-day trial—no credit card required.</p>
          <Button size="lg" onClick={() => navigate('/signup')} data-testid="guides-cta-button">
            Start Free 30-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </MarketingLayout>
  );
}
